/**
 * ObsidianConnection — pont Synapse ↔ Obsidian via le plugin « Local REST API »
 *
 * Deux modes :
 *  - LOCAL   : Obsidian déployé en sidecar DANS le pod Synapse. Un clic
 *              « Provisionner » génère la clé API + configure l'URL interne
 *              (127.0.0.1:27123) automatiquement, et fournit le manifeste K8s.
 *  - EXTERNE : Obsidian tourne sur un poste distant. Le commandant renseigne
 *              manuellement l'URL du plugin et la clé API.
 *
 * Dans les deux cas : test de connexion réel via POST /api/obsidian/test,
 * statut live (pastille + latence + authentifié).
 */

import { useState, useEffect, useCallback } from "react";
import { api } from "../lib/api";
import { showToast } from "./Toast";

const BASE = import.meta.env.VITE_API_URL || "/api";

async function putSetting(key: string, value: string, isSecret = false) {
  try {
    await fetch(`${BASE}/settings/${encodeURIComponent(key)}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ value, isSecret }),
    });
  } catch { /* best-effort */ }
}

type ConnState = "unknown" | "testing" | "ok" | "auth_fail" | "down" | "disabled";
type Mode = "local" | "external";

export default function ObsidianConnection({
  initialEnabled,
  initialMode,
  initialUrl,
  urlPersisted,
  keyPreview,
  keyPersisted,
}: {
  initialEnabled: boolean;
  initialMode: Mode;
  initialUrl: string;
  urlPersisted: boolean;
  keyPreview?: string;
  keyPersisted: boolean;
}) {
  const [enabled, setEnabled] = useState(initialEnabled);
  const [mode, setMode] = useState<Mode>(initialMode);
  const [url, setUrl] = useState(initialUrl);
  const [apiKey, setApiKey] = useState(""); // vide = ne pas réécrire le secret
  const [state, setState] = useState<ConnState>(initialEnabled ? "unknown" : "disabled");
  const [detail, setDetail] = useState<string>("");
  const [latency, setLatency] = useState<number | null>(null);
  const [provisioning, setProvisioning] = useState(false);
  const [manifest, setManifest] = useState<string | null>(null);
  const [localKeyPreview, setLocalKeyPreview] = useState<string | undefined>(
    initialMode === "local" ? keyPreview : undefined,
  );

  const runTest = useCallback(async (silent = false) => {
    if (!url.trim()) {
      setState("down");
      setDetail("URL non renseignée");
      return;
    }
    setState("testing");
    try {
      const res = await api.obsidian.test(url.trim(), apiKey.trim() || undefined);
      setLatency(res.latencyMs ?? null);
      setDetail(res.message);
      if (res.ok) {
        setState("ok");
        if (!silent) showToast("success", "Obsidian connecté", res.message);
      } else if (res.authenticated === false) {
        setState("auth_fail");
        if (!silent) showToast("error", "Non authentifié", res.message);
      } else {
        setState("down");
        if (!silent) showToast("error", "Obsidian injoignable", res.message);
      }
    } catch (err) {
      setState("down");
      setDetail(err instanceof Error ? err.message : "Erreur réseau");
      if (!silent) showToast("error", "Échec du test", detail);
    }
  }, [url, apiKey, detail]);

  // Auto-test au montage si activé et déjà configuré
  useEffect(() => {
    if (initialEnabled && urlPersisted && keyPersisted) {
      runTest(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleEnabled = async (v: boolean) => {
    setEnabled(v);
    setState(v ? "unknown" : "disabled");
    await putSetting("obsidian.enabled", v ? "true" : "false", false);
    showToast("saved", "Sauvegardé", `Connexion Obsidian ${v ? "activée" : "désactivée"}`);
  };

  const changeMode = async (m: Mode) => {
    setMode(m);
    setManifest(null);
    await putSetting("obsidian.mode", m, false);
    if (m === "external") {
      // En externe, on repart d'une URL saisissable (on ne force plus l'URL interne).
      showToast("saved", "Mode externe", "Renseigne l'URL du plugin et la clé manuellement.");
    }
  };

  const provisionLocal = async () => {
    setProvisioning(true);
    try {
      const res = await api.obsidian.provisionLocal();
      setUrl(res.apiUrl);
      setManifest(res.sidecarManifest);
      setLocalKeyPreview(res.keyPreview);
      setEnabled(true);
      setState("unknown");
      showToast("success", "Obsidian local provisionné", res.message);
    } catch (err) {
      showToast("error", "Échec provisioning", err instanceof Error ? err.message : "Erreur");
    } finally {
      setProvisioning(false);
    }
  };

  const saveUrl = async () => { await putSetting("obsidian.api_url", url.trim(), false); };
  const saveKey = async () => {
    if (!apiKey.trim()) return;
    await putSetting("obsidian.api_key", apiKey.trim(), true);
    showToast("saved", "Sauvegardé", "Clé API Obsidian enregistrée");
  };

  const STATE_META: Record<ConnState, { label: string; color: string; icon: string }> = {
    unknown: { label: "Non testé", color: "var(--muted)", icon: "○" },
    testing: { label: "Test en cours…", color: "var(--amber)", icon: "⟳" },
    ok: { label: "Connecté & authentifié", color: "var(--olive)", icon: "✓" },
    auth_fail: { label: "Endpoint OK · clé refusée", color: "var(--amber)", icon: "⚠" },
    down: { label: "Injoignable", color: "var(--rust)", icon: "✕" },
    disabled: { label: "Désactivé", color: "var(--muted)", icon: "—" },
  };
  const sm = STATE_META[state];

  return (
    <div className="settings-card">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h3 style={{ margin: 0 }}>🔌 Connexion — Local REST API</h3>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span style={{
            width: "9px", height: "9px", borderRadius: "999px", background: sm.color,
            boxShadow: state === "ok" ? "0 0 8px var(--olive)" : undefined,
          }} />
          <span style={{ fontSize: "12px", color: sm.color, fontWeight: 600 }}>
            {state === "testing" ? <span style={{ animation: "spin 1s linear infinite", display: "inline-block" }}>⟳</span> : sm.icon} {sm.label}
          </span>
        </div>
      </div>

      {/* Toggle activation */}
      <div className="field-group">
        <label>Pont Synapse → Obsidian</label>
        <div className="select-row">
          <span className={`select-pill ${enabled ? "active" : ""}`} style={{ cursor: "pointer" }} onClick={() => toggleEnabled(true)}>Activé</span>
          <span className={`select-pill ${!enabled ? "active" : ""}`} style={{ cursor: "pointer" }} onClick={() => toggleEnabled(false)}>Désactivé</span>
        </div>
      </div>

      {/* Sélecteur de mode */}
      <div className="field-group">
        <label>Emplacement d'Obsidian</label>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
          <div onClick={() => enabled && changeMode("local")} style={{
            padding: "12px", borderRadius: "14px", cursor: enabled ? "pointer" : "not-allowed",
            opacity: enabled ? 1 : 0.5,
            background: mode === "local" ? "rgba(94,199,168,0.1)" : "rgba(0,0,0,0.14)",
            border: `1px solid ${mode === "local" ? "rgba(94,199,168,0.35)" : "var(--line)"}`,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", fontWeight: 600 }}>
              📦 Local (dans le pod)
              {mode === "local" && <span style={{ marginLeft: "auto", color: "var(--olive)" }}>✓</span>}
            </div>
            <div style={{ fontSize: "10px", color: "var(--muted)", marginTop: "4px", lineHeight: 1.4 }}>
              Sidecar auto-déployé. URL + clé configurées automatiquement.
            </div>
          </div>
          <div onClick={() => enabled && changeMode("external")} style={{
            padding: "12px", borderRadius: "14px", cursor: enabled ? "pointer" : "not-allowed",
            opacity: enabled ? 1 : 0.5,
            background: mode === "external" ? "rgba(107,141,214,0.12)" : "rgba(0,0,0,0.14)",
            border: `1px solid ${mode === "external" ? "rgba(107,141,214,0.35)" : "var(--line)"}`,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", fontWeight: 600 }}>
              🌐 Externe (poste distant)
              {mode === "external" && <span style={{ marginLeft: "auto", color: "var(--blue)" }}>✓</span>}
            </div>
            <div style={{ fontSize: "10px", color: "var(--muted)", marginTop: "4px", lineHeight: 1.4 }}>
              Config manuelle de l'URL et de la clé API.
            </div>
          </div>
        </div>
      </div>

      {/* ── MODE LOCAL ── */}
      {mode === "local" && (
        <>
          <div style={{
            padding: "12px 14px", borderRadius: "14px", background: "rgba(94,199,168,0.06)",
            border: "1px solid rgba(94,199,168,0.2)", fontSize: "12px", color: "var(--muted)", lineHeight: 1.5,
          }}>
            En mode local, Synapse déploie Obsidian en <strong>sidecar</strong> dans le pod et
            configure <strong>automatiquement</strong> l'URL interne (<code>127.0.0.1:27123</code>)
            et la clé API. Aucune saisie manuelle.
          </div>

          <div className="field-group">
            <label>URL interne (auto)</label>
            <input className="field-input" value={url || "http://127.0.0.1:27123"} readOnly disabled style={{ opacity: 0.7 }} />
          </div>
          <div className="field-group">
            <label>Clé API (auto-générée)</label>
            <input className="field-input" type="text" readOnly disabled
              value={localKeyPreview ? `Enregistrée : ${localKeyPreview}` : (keyPersisted ? "Enregistrée : ••••••••" : "Non provisionnée")}
              style={{ opacity: 0.7 }} />
          </div>

          <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
            <button className="btn primary" style={{ fontSize: "12px", padding: "8px 14px" }}
              onClick={provisionLocal} disabled={!enabled || provisioning}>
              {provisioning ? "⟳ Provisioning…" : "⚙ Provisionner automatiquement"}
            </button>
            <button className="btn" style={{ fontSize: "12px", padding: "8px 14px" }}
              onClick={() => runTest(false)} disabled={!enabled || state === "testing"}>
              {state === "testing" ? "⟳ Test…" : "🔍 Tester"}
            </button>
            {detail && state !== "testing" && (
              <span style={{ fontSize: "11px", color: sm.color, fontFamily: "var(--mono)" }}>{detail}</span>
            )}
          </div>

          {manifest && (
            <div className="field-group">
              <label>Manifeste sidecar à appliquer (K8s)</label>
              <pre style={{
                background: "rgba(0,0,0,0.28)", border: "1px solid var(--line)", borderRadius: "12px",
                padding: "12px", fontFamily: "var(--mono)", fontSize: "10px", lineHeight: 1.5,
                overflow: "auto", maxHeight: "260px", color: "#c9d4ff", margin: 0,
              }}>{manifest}</pre>
              <div style={{ display: "flex", gap: "8px" }}>
                <button className="btn" style={{ fontSize: "11px", padding: "6px 10px" }}
                  onClick={() => { navigator.clipboard?.writeText(manifest); showToast("success", "Copié", "Manifeste copié dans le presse-papier"); }}>
                  📋 Copier le manifeste
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* ── MODE EXTERNE ── */}
      {mode === "external" && (
        <>
          <div className="field-group">
            <label>URL du plugin (HTTPS)</label>
            <input
              className="field-input"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onBlur={saveUrl}
              placeholder="https://obsidian.poste.local:27124"
              disabled={!enabled}
            />
          </div>
          <div className="field-group">
            <label>Clé API (Bearer)</label>
            <input
              className="field-input"
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              onBlur={saveKey}
              placeholder={keyPersisted ? `Enregistrée : ${keyPreview ?? "••••••••"} — saisir pour remplacer` : "Coller la clé du plugin"}
              disabled={!enabled}
            />
          </div>
          <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
            <button className="btn primary" style={{ fontSize: "12px", padding: "8px 14px" }}
              onClick={() => runTest(false)} disabled={!enabled || state === "testing"}>
              {state === "testing" ? "⟳ Test…" : "🔍 Tester la connexion"}
            </button>
            {latency != null && state !== "testing" && (
              <span style={{ fontSize: "11px", color: "var(--muted)", fontFamily: "var(--mono)" }}>{latency}ms</span>
            )}
            {detail && state !== "testing" && (
              <span style={{ fontSize: "11px", color: sm.color, fontFamily: "var(--mono)" }}>{detail}</span>
            )}
          </div>
        </>
      )}
    </div>
  );
}
